import {
  Alert,
  AlertVariants,
  Attributes,
  AttributesRow,
  Button,
  closeCurrentDialog,
  Dropdown,
  DropdownItem,
  openDialog,
  Segment,
  Separator,
} from 'platform/components';
import {
  Box,
  Heading,
  HStack,
  Icon,
  Link,
  Show,
  Space,
  Text,
  ThemeIconKey,
  VStack,
} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';

import {isNotNil, last} from 'ramda';

import {useRenderCorrectiveTaxDocumentMutation} from '@dms/api/finance';
import {
  CheckoutNotificationButton,
  FileMetadata,
  Payment,
  useCallNotificationActionMutation,
  useGetCheckoutPaymentsQuery,
  usePostCheckoutPaymentCancelMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {accountingRoutes} from '@dms/routes';
import {
  EMPTY_PLACEHOLDER,
  handleApiError,
  type UploadedCustomerMessageFile,
  useBank,
  useCashRegister,
} from '@dms/shared';

import {
  buildArray,
  composePath,
  openFile,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
  useBoolean,
  useNavigate,
} from 'shared/web';

import {SendAfterSalesEmailForm} from '../../SendAftersalesEmailForm/SendAftersalesEmailForm';
import {PayDialog} from './PayDialog';
import {PaymentsList} from './PaymentsList';

const NOTIFICATION_VARIANT_MAP: Record<'ERROR' | 'WARNING' | 'SUCCESS' | 'INFO', AlertVariants> = {
  ERROR: 'error',
  WARNING: 'warning',
  SUCCESS: 'success',
  INFO: 'info',
};

interface PaymentOverviewProps extends RequiredTestIdProps {
  checkoutId: string;
  payment: Payment;
  onAddAnotherDepositClick?: () => Promise<unknown>;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
  isCorrectivePayment?: boolean;
  serviceCaseId?: string;
  serviceOrderId?: string;
}

export function PaymentOverview(props: PaymentOverviewProps) {
  const [selectedSegment, setSegment] = useState('DETAILS');
  const navigate = useNavigate();
  const location = useLocation();
  const {refetch: refetchCheckoutPayments} = useGetCheckoutPaymentsQuery({
    checkoutId: props.checkoutId,
  });

  const [isAddLoading, startAddLoading, stopAddLoading] = useBoolean();

  const {getCashRegisterById} = useCashRegister();
  const {composeBankAccount} = useBank();
  const formatCurrency = useFormatCurrency();
  const formatDateTime = useDateTimeFormatter();

  const [postCheckoutPaymentCancel, {isLoading: isCancelingPayment}] =
    usePostCheckoutPaymentCancelMutation();
  const [renderCorrectiveTaxDocument] = useRenderCorrectiveTaxDocumentMutation();

  const handleOpenCorrectiveTaxDocument = (correctiveTaxDocumentId: string) =>
    renderCorrectiveTaxDocument({
      renderDocumentRequestBody: {
        documentId: correctiveTaxDocumentId,
      },
    })
      .unwrap()
      .then((res) => res.pdfUrl)
      .then(openFile)
      .catch(handleApiError);

  const handleCancelPayment = () =>
    postCheckoutPaymentCancel({
      checkoutId: props.checkoutId,
      paymentId: props.payment.checkoutPaymentId,
    })
      .unwrap()
      .catch(handleApiError);

  const handleRefresh = () => {
    refetchCheckoutPayments();
  };

  const handleSendInvoiceDocument = (invoice: FileMetadata) => {
    if (!props.serviceCaseId) {
      return;
    }

    const attachment: UploadedCustomerMessageFile = {
      fileId: invoice.fileId,
      fileName: invoice.name,
      fileType: 'application/pdf',
      fileUrl: invoice.fileUrl,
      shouldConvertToPdf: false,
    };

    openDialog(
      <SendAfterSalesEmailForm
        serviceCaseId={props.serviceCaseId}
        serviceOrderId={props.serviceOrderId}
        defaultValues={{attachments: [attachment]}}
      />,
      {
        title: i18n.t('general.actions.sendDocument'),
        size: 'large',
      }
    );
  };

  const [callNotificationAction] = useCallNotificationActionMutation();
  const notificationActionInFlightRef = useRef<Set<string>>(new Set());

  const handleNotificationButtonClick = (button: CheckoutNotificationButton) => {
    if (button.action === 'NAVIGATE') {
      const url = button.navigateUrl?.trim();
      if (url) {
        if (button.actionTarget === 'NEW_TAB') {
          window.open(url, '_blank');
        } else {
          navigate(url);
        }
      }
      return;
    }

    if (button.action === 'API_CALL' && button.apiCall) {
      const actionKey = `${button.apiCall.method}:${button.apiCall.path}`;
      if (notificationActionInFlightRef.current.has(actionKey)) {
        return;
      }
      notificationActionInFlightRef.current.add(actionKey);
      let invalidateScope: 'paymentsList' | 'checkout' | undefined;
      if (button.onSuccess === 'CHECKOUT_PAYMENT_REFRESH') {
        invalidateScope = 'paymentsList';
      } else if (button.onSuccess === 'CHECKOUT_REFRESH') {
        invalidateScope = 'checkout';
      }
      callNotificationAction({
        path: button.apiCall.path,
        method: button.apiCall.method,
        payload: button.apiCall.payload,
        checkoutId: props.checkoutId,
        invalidateScope,
      })
        .unwrap()
        .then(() => {
          if (button.onSuccess === 'PAGE_REFRESH') {
            window.location.reload();
          }
        })
        .catch(handleApiError)
        .finally(() => {
          notificationActionInFlightRef.current.delete(actionKey);
        });
    }
  };

  const handleCreateCorrectiveTaxDocument = (
    correctiveTaxDocumentType: 'full-correction' | 'partial-correction'
  ) => {
    navigate(
      composePath(accountingRoutes.newCorrectiveTaxDocumentForAftersales, {
        params: {
          checkoutId: props.checkoutId,
          paymentId: props.payment.checkoutPaymentId,
        },
        queryParams: {
          correctiveTaxDocumentType,
          returnUrl: location.pathname + location.search,
        },
      })
    );
  };

  const paymentMethodLabel = match(props.payment.method)
    .with('BANK_TRANSFER', () => i18n.t('entity.invoice.paymentMethod.bankTransfer'))
    .with('CARD', () => i18n.t('entity.invoice.paymentMethod.card'))
    .with('CASH', () => i18n.t('entity.invoice.paymentMethod.cash'))
    .with('INTERNAL', () => i18n.t('general.labels.internal'))
    .exhaustive();

  const rows = buildArray<AttributesRow>([
    {
      label: i18n.t('entity.checkout.labels.paymentMethod'),
      value: paymentMethodLabel,
      testId: 'paymentMethod',
    },
    {
      label: i18n.t('entity.checkout.labels.issuedOn'),
      value: props.payment.issuedOn
        ? formatDateTime('dateShort', parseDate(props.payment.issuedOn))
        : EMPTY_PLACEHOLDER,
      testId: 'issuedOn',
    },
    {
      label: i18n.t('entity.checkout.labels.dueDate'),
      value: props.payment.dueDate
        ? formatDateTime('dateShort', parseDate(props.payment.dueDate))
        : EMPTY_PLACEHOLDER,
      testId: 'dueDate',
    },
    {
      label: i18n.t('entity.accounting.labels.dateOfTaxableSupply'),
      value: props.payment.dateOfTaxableSupply
        ? formatDateTime('dateShort', parseDate(props.payment.dateOfTaxableSupply))
        : EMPTY_PLACEHOLDER,
      testId: 'dateOfTaxableSupply',
    },
  ])
    .when(props.payment.method === 'CASH', {
      label: i18n.t('entity.checkout.labels.cashRegister'),
      value: props.payment.cashRegisterId
        ? getCashRegisterById(props.payment.cashRegisterId)?.name
        : EMPTY_PLACEHOLDER,
    })
    .when(props.payment.method === 'BANK_TRANSFER', {
      label: i18n.t('general.labels.bankAccount'),
      value: props.payment.bankAccount
        ? composeBankAccount(props.payment.bankAccount)
        : EMPTY_PLACEHOLDER,
    })
    .add({
      label: i18n.t('general.labels.note'),
      value: props.payment.note,
    })
    .whenNotNil(props.payment.invoices, (invoices) =>
      invoices.map((invoice) => ({
        label: i18n.t('entity.checkout.labels.invoice'),
        content: (
          <HStack justify="space-between" align="center">
            <Link size="small" onClick={() => openFile(invoice.fileUrl)} title={invoice.name} />
            <HStack spacing={2} align="center">
              <Show when={props.payment.isCancellingAllowed}>
                <Button
                  size="small"
                  isLoading={isCancelingPayment}
                  variant="dangerLink"
                  isDisabled={props.isCompletingDisabled || props.isEditingDisabled}
                  title={i18n.t('entity.invoice.actions.cancelInvoice')}
                  onClick={handleCancelPayment}
                />
              </Show>
              <Show when={isNotNil(props.serviceCaseId)}>
                <Link
                  size="small"
                  title={i18n.t('general.actions.sendDocument')}
                  onClick={() => handleSendInvoiceDocument(invoice)}
                  leftIcon="communication/email"
                />
              </Show>
              <Dropdown
                closeOnBlur
                dropdownControl={
                  <Link
                    size="small"
                    title={i18n.t('entity.checkout.actions.correctiveTaxDocument')}
                    rightIcon="navigation/expand_more"
                  />
                }
              >
                <DropdownItem
                  onClick={() => handleCreateCorrectiveTaxDocument('full-correction')}
                  label={i18n.t('entity.accounting.labels.correctiveTaxDocumentFull')}
                />
              </Dropdown>
            </HStack>
          </HStack>
        ),
        testId: 'invoice',
      }))
    )
    .whenNotNil(props.payment.proformaInvoices, (proformaInvoices) =>
      proformaInvoices.map((invoice) => ({
        label: i18n.t('entity.checkout.labels.proformaInvoice'),
        content: (
          <HStack justify="space-between">
            <Link size="small" onClick={() => openFile(invoice.fileUrl)} title={invoice.name} />
            <HStack spacing={2} align="center">
              <Show when={props.payment.isCancellingAllowed}>
                <Button
                  size="small"
                  isLoading={isCancelingPayment}
                  variant="dangerLink"
                  isDisabled={props.isCompletingDisabled || props.isEditingDisabled}
                  title={i18n.t('entity.invoice.actions.cancelInvoice')}
                  onClick={handleCancelPayment}
                />
              </Show>
              <Show when={isNotNil(props.serviceCaseId)}>
                <Link
                  size="small"
                  title={i18n.t('general.actions.sendDocument')}
                  onClick={() => handleSendInvoiceDocument(invoice)}
                  leftIcon="communication/email"
                />
              </Show>
            </HStack>
          </HStack>
        ),
        testId: 'proformaInvoice',
      }))
    )
    .whenNotNil(props.payment.internalInvoices, (internalInvoices) =>
      internalInvoices.map((invoice) => ({
        label: i18n.t('entity.checkout.labels.internalInvoiceDocument'),
        content: (
          <HStack justify="space-between">
            <Link size="small" onClick={() => openFile(invoice.fileUrl)} title={invoice.name} />
            <HStack spacing={2} align="center">
              <Show when={props.payment.isCancellingAllowed}>
                <Button
                  size="small"
                  isLoading={isCancelingPayment}
                  variant="dangerLink"
                  isDisabled={props.isCompletingDisabled || props.isEditingDisabled}
                  title={i18n.t('entity.invoice.actions.cancelInvoice')}
                  onClick={handleCancelPayment}
                />
              </Show>
              <Show when={isNotNil(props.serviceCaseId)}>
                <Link
                  size="small"
                  title={i18n.t('general.actions.sendDocument')}
                  onClick={() => handleSendInvoiceDocument(invoice)}
                  leftIcon="communication/email"
                />
              </Show>
              <Dropdown
                closeOnBlur
                dropdownControl={
                  <Link
                    size="small"
                    title={i18n.t('entity.checkout.actions.correctiveTaxDocument')}
                    rightIcon="navigation/expand_more"
                  />
                }
              >
                <DropdownItem
                  onClick={() => handleCreateCorrectiveTaxDocument('full-correction')}
                  label={i18n.t('entity.accounting.labels.correctiveTaxDocumentFull')}
                />
              </Dropdown>
            </HStack>
          </HStack>
        ),
        testId: 'internalInvoice',
      }))
    )
    .when(props.payment.correctiveTaxDocumentId, {
      label: i18n.t('entity.checkout.labels.correctiveTaxDocument'),
      content: (
        <HStack justify="space-between">
          <Link
            size="small"
            onClick={() =>
              handleOpenCorrectiveTaxDocument(props.payment.correctiveTaxDocumentId ?? '')
            }
            title={props.payment.correctiveTaxDocuments?.[0]?.name ?? ''}
            isDisabled={!props.payment.correctiveTaxDocumentId}
          />
          <HStack spacing={2} align="center">
            <Show when={props.payment.isCancellingAllowed}>
              <Button
                size="small"
                isLoading={isCancelingPayment}
                variant="dangerLink"
                isDisabled={props.isCompletingDisabled || props.isEditingDisabled}
                title={i18n.t('entity.invoice.actions.cancelInvoice')}
                onClick={handleCancelPayment}
              />
            </Show>
            <Show when={isNotNil(props.serviceCaseId)}>
              <Link
                size="small"
                title={i18n.t('general.actions.sendDocument')}
                onClick={() => {
                  const doc = props.payment.correctiveTaxDocuments?.[0];
                  if (isNotNil(doc)) {
                    handleSendInvoiceDocument(doc as FileMetadata);
                  }
                }}
                leftIcon="communication/email"
              />
            </Show>
          </HStack>
        </HStack>
      ),
      testId: 'internalInvoice',
    });

  const handleAddAnotherDeposit = () => {
    if (!props.onAddAnotherDepositClick) {
      return;
    }

    startAddLoading();
    props.onAddAnotherDepositClick().finally(stopAddLoading);
  };

  const paymentAmount = match(props.payment)
    .with(
      {foreignCurrencyPaymentAmount: Pattern.not(Pattern.nullish)},
      ({foreignCurrencyPaymentAmount}) => foreignCurrencyPaymentAmount
    )
    .with(
      {roundedPaymentAmount: Pattern.not(Pattern.nullish)},
      ({roundedPaymentAmount}) => roundedPaymentAmount
    )
    .otherwise(({paymentAmount}) => paymentAmount);

  const remainingPaymentAmount =
    props.payment.remainingForeignCurrencyPaymentAmount ?? props.payment.remainingPaymentAmount;

  const directionIcon = match<'incoming' | 'outgoing', ThemeIconKey | undefined>(
    props.payment.direction
  )
    .with('incoming', () => 'navigation/arrow_forward')
    .with('outgoing', () => 'navigation/arrow_back')
    .otherwise(() => undefined);

  return (
    <VStack spacing={4} align="flex-start">
      <Show when={props.payment.documentState === 'request-failed'}>
        <Alert
          layout="detailed"
          variant="error"
          title={i18n.t('entity.checkout.labels.billingDocumentError')}
          actions={[
            {
              title: i18n.t('general.actions.refresh'),
              onClick: handleRefresh,
            },
          ]}
        />
      </Show>
      <Show when={!!props.payment.notifications?.length}>
        <VStack spacing={2} width="100%">
          {props.payment.notifications?.map((notification, index) => (
            <Alert
              key={notification.code ?? index}
              layout="detailed"
              variant={NOTIFICATION_VARIANT_MAP[notification.type]}
              title={notification.message}
              actions={notification.buttons?.map((button) => ({
                title: button.label,
                onClick: () => handleNotificationButtonClick(button),
              }))}
              data-testid={suffixTestId(`notification-${index}`, props)}
            />
          ))}
        </VStack>
      </Show>
      <HStack spacing={20} width="100%">
        <VStack spacing={1}>
          <Text size="xSmall" color="secondary">
            {i18n.t('general.labels.amount')}
          </Text>
          <Heading size={4} data-testid={suffixTestId('amount', props)}>
            {formatCurrency(paymentAmount.amount, paymentAmount.currency, 2)}
          </Heading>
        </VStack>
        <Show when={remainingPaymentAmount.amount > 0}>
          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.invoice.labels.remaining')}
            </Text>
            <Heading size={4} data-testid={suffixTestId('remainingAmount', props)} color="danger">
              {formatCurrency(remainingPaymentAmount.amount, remainingPaymentAmount.currency, 2)}
            </Heading>
          </VStack>
        </Show>
        <Show when={props.isCorrectivePayment}>
          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('general.labels.direction')}
            </Text>
            <HStack align="center" spacing={1}>
              <Icon value={directionIcon} size={3} />
              <Text data-testid={suffixTestId('paymentDirection', props)}>
                {i18n.t(`entity.prescription.labels.${props.payment.direction}`)}
              </Text>
            </HStack>
          </VStack>
        </Show>
      </HStack>
      <Separator />
      <VStack spacing={4} justify="flex-start" width="100%">
        <HStack justify="space-between">
          <Box width={75}>
            <Segment
              value={selectedSegment}
              onChange={setSegment}
              options={[
                {label: i18n.t('general.labels.details'), value: 'DETAILS'},
                {label: i18n.t('page.accounting.invoiceDetailPayments.title'), value: 'PAYMENTS'},
              ]}
              data-testid={props['data-testid']}
            />
          </Box>
          <Show when={!!props.payment.documentNavigateUrl}>
            <Button
              variant="link"
              rightIcon="action/open_in_new"
              title={i18n.t('entity.checkout.actions.goToDocument')}
              onClick={() => window.open(props.payment.documentNavigateUrl!, '_blank')}
              data-testid={suffixTestId('goToDocument', props)}
            />
          </Show>
        </HStack>
        <Show when={selectedSegment === 'DETAILS'}>
          <Attributes size="quarter" rows={rows} data-testid={props['data-testid']} />
        </Show>
        <Show when={selectedSegment === 'PAYMENTS'}>
          <PaymentsList
            data-testid={suffixTestId('payments', props)}
            checkoutId={props.checkoutId}
            payment={props.payment}
            isEditingDisabled={props.isEditingDisabled}
            isCompletingDisabled={props.isCompletingDisabled}
          />
        </Show>
      </VStack>
      <Show when={props.payment.paymentState === 'PENDING' || !!props.onAddAnotherDepositClick}>
        <Separator spacing={0} />
        <HStack width="100%">
          {props.onAddAnotherDepositClick && (
            <Button
              variant="link"
              title={i18n.t('entity.checkout.addAnotherPayment')}
              leftIcon="content/add_circle"
              onClick={handleAddAnotherDeposit}
              isDisabled={isAddLoading || props.isEditingDisabled}
              isLoading={isAddLoading}
            />
          )}

          <Space fillAvailable />
          <HStack spacing={2}>
            <Show when={props.payment.paymentState === 'PENDING'}>
              <Button
                title={i18n.t('entity.order.actions.pay')}
                isDisabled={props.isEditingDisabled || props.isCompletingDisabled}
                onClick={() =>
                  openDialog(
                    <PayDialog
                      checkoutId={props.checkoutId}
                      payment={props.payment}
                      onClose={closeCurrentDialog}
                      data-testid={props['data-testid']}
                    />,
                    {
                      title: i18n.t('entity.invoice.labels.paymentOfInvoice', {
                        number: last(props.payment.files ?? [])?.name,
                      }),
                    }
                  )
                }
              />
            </Show>
          </HStack>
        </HStack>
      </Show>
    </VStack>
  );
}
